import { Link } from '@tanstack/react-router'
import { Check, Inbox } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { toast } from '@/components/ui'
import { useDisclosure } from '@/components/layout/useDisclosure'
import { cn } from '@/lib/utils'

import type { PresenceOverview, PresenceStatus } from './api'
import { isPresenceUnavailableForUser, usePresence, useSetPresenceStatus } from './queries'

/** Linha de status da equipe (o que o cliente vive agora): online, fora do horário ou sem ninguém. */
export function TeamStatusText({ overview }: { readonly overview: PresenceOverview }) {
  const { t } = useTranslation()
  const { team } = overview

  if (team.state === 'available') {
    return <>{t('presence.team.available', { count: team.onlineOperators })}</>
  }

  if (team.state === 'outside_business_hours') {
    return <>{t('presence.team.outsideHours', { when: team.returnText })}</>
  }

  return <>{t('presence.team.nobodyOnline')}</>
}

/**
 * Alerta de NOVO chamado: toast + contador no título da aba ("(2) Codout.Support") enquanto houver chamados aguardando.
 * O título é o que o atendente vê quando está em outra aba — o mesmo recurso que Chatwoot/Intercom usam.
 */
function useWaitingTicketsAlert(waiting: number | undefined, iAmOnline: boolean) {
  const { t } = useTranslation()
  const previous = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (waiting === undefined) {
      return
    }

    if (previous.current !== undefined && waiting > previous.current && iAmOnline) {
      toast.info(t('presence.newTicket', { count: waiting }))
    }

    previous.current = waiting
  }, [waiting, iAmOnline, t])

  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s/, '')
    document.title = waiting !== undefined && waiting > 0 ? `(${String(waiting)}) ${base}` : base

    return () => {
      document.title = document.title.replace(/^\(\d+\)\s/, '')
    }
  }, [waiting])
}

/**
 * Controle de presença do atendente na topbar (ADR-027): Disponível/Ausente, status da equipe e a fila de chamados
 * aguardando. Mantém o heartbeat enquanto o app está aberto. Some para quem não atende conversas.
 */
export function PresenceControl({
  tenantId,
  tenantSlug,
}: {
  readonly tenantId: string
  readonly tenantSlug: string
}) {
  const { t } = useTranslation()
  const presence = usePresence(tenantId)
  const setStatus = useSetPresenceStatus(tenantId)
  const { isOpen, setIsOpen, containerRef } = useDisclosure()

  useWaitingTicketsAlert(presence.data?.waitingTickets, presence.data?.iAmOnline ?? false)

  if (isPresenceUnavailableForUser(presence.error) || presence.data === undefined) {
    return null
  }

  const overview = presence.data
  const online = overview.iAmOnline

  function choose(status: PresenceStatus): void {
    setIsOpen(false)
    if (status === overview.myStatus) {
      return
    }

    setStatus.mutate(status, {
      onError: () => {
        toast.error(t('presence.changeFailed'))
      },
    })
  }

  return (
    <div className="flex items-center gap-2">
      {overview.waitingTickets > 0 ? (
        <Link
          to="/app/$tenantSlug/desk"
          params={{ tenantSlug }}
          className="inline-flex items-center gap-1.5 rounded-full bg-status-warning-subtle px-2.5 py-1 text-xs font-medium text-status-warning hover:opacity-90"
        >
          <Inbox aria-hidden="true" className="h-3.5 w-3.5" />
          {t('presence.waiting', { count: overview.waitingTickets })}
        </Link>
      ) : null}

      <div ref={containerRef} className="relative">
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label={t('presence.openLabel')}
          onClick={() => {
            setIsOpen((value) => !value)
          }}
          className="inline-flex h-8 items-center gap-2 rounded-full border border-border-default px-3 text-xs font-medium text-foreground-secondary hover:bg-background-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
        >
          <span
            aria-hidden="true"
            className={cn(
              'h-2 w-2 rounded-full',
              online ? 'bg-status-success' : 'bg-foreground-muted',
            )}
          />
          {overview.myStatus === 'available' ? t('presence.available') : t('presence.away')}
        </button>

        {isOpen ? (
          <div
            role="menu"
            className="absolute right-0 z-10 mt-1 w-72 rounded-md border border-border-default bg-background-elevated py-1 shadow-sm"
          >
            {(['available', 'away'] as const).map((status) => (
              <button
                key={status}
                type="button"
                role="menuitemradio"
                aria-checked={overview.myStatus === status}
                disabled={setStatus.isPending}
                onClick={() => {
                  choose(status)
                }}
                className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-background-subtle disabled:opacity-60"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    status === 'available' ? 'bg-status-success' : 'bg-foreground-muted',
                  )}
                />
                <span className="flex flex-1 flex-col">
                  <span className="text-sm font-medium text-foreground-primary">
                    {status === 'available' ? t('presence.available') : t('presence.away')}
                  </span>
                  <span className="text-xs text-foreground-muted">
                    {status === 'available' ? t('presence.availableHint') : t('presence.awayHint')}
                  </span>
                </span>
                {overview.myStatus === status ? (
                  <Check aria-hidden="true" className="mt-1 h-4 w-4 text-foreground-muted" />
                ) : null}
              </button>
            ))}
            <div className="my-1 border-t border-border-default" />
            <p className="px-3 py-2 text-xs text-foreground-muted">
              <TeamStatusText overview={overview} />
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}
